import { Modal, Stack, Text, Loader, ThemeIcon, Group, Box, Paper, Center, Button } from '@mantine/core';
import { MaterialSymbol } from 'react-material-symbols';
import { useEffect, useState, useCallback, useRef } from 'react';
import { OrderDTO } from '../../../interfaces/OrderInterfaces';
import { acquireTokenSilent } from '../../../services/authService';

interface CreationStep {
    type: 'SEARCH_CUSTOMER' | 'CREATE_CUSTOMER' | 'CREATE_SHIPPING_ADDRESS' | 'CREATE_ORDER' | 'COMPLETED' | 'ERROR';
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';
    message: string;
    details?: string;
}

interface MexalOrderCreationModalProps {
    opened: boolean;
    onClose: () => void;
    order: OrderDTO | null;
    onSuccess?: (documentId: string) => void;
}

const STEP_CONFIG: Record<string, { icon: string; label: string }> = {
    SEARCH_CUSTOMER: { icon: 'person_search', label: 'Ricerca Cliente' },
    CREATE_CUSTOMER: { icon: 'person_add', label: 'Creazione Cliente' },
    CREATE_SHIPPING_ADDRESS: { icon: 'location_on', label: 'Indirizzo di Spedizione' },
    CREATE_ORDER: { icon: 'receipt_long', label: 'Creazione Ordine' },
};

const MexalOrderCreationModal = ({ opened, onClose, order, onSuccess }: MexalOrderCreationModalProps) => {
    const [steps, setSteps] = useState<CreationStep[]>([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isError, setIsError] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const startCreation = useCallback(() => {
        if (!order) return;

        setSteps([]);
        setIsCompleted(false);
        setIsError(false);
        setDocumentId(null);

        // EventSource cannot send the Bearer token required by the orders service.
        // Use fetch and parse the SSE response so this request follows the same Entra
        // authentication flow as the other orders API calls.
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        let receivedTerminalEvent = false;

        const reportError = (message: string, details?: string) => {
            setIsError(true);
            setSteps(prev => [...prev, {
                type: 'ERROR',
                status: 'ERROR',
                message,
                details
            }]);
        };

        const handleStep = (step: CreationStep) => {
            if (step.type === 'COMPLETED') {
                receivedTerminalEvent = true;
                setIsCompleted(true);
                setDocumentId(step.details || null);
                if (step.details && onSuccess) {
                    onSuccess(step.details);
                }
            } else if (step.type === 'ERROR') {
                receivedTerminalEvent = true;
                setIsError(true);
                setSteps(prev => [...prev, step]);
            } else {
                setSteps(prev => {
                    const existingIndex = prev.findIndex(s => s.type === step.type);
                    if (existingIndex >= 0) {
                        const updated = [...prev];
                        updated[existingIndex] = step;
                        return updated;
                    }
                    return [...prev, step];
                });
            }
        };

        const processSseEvent = (rawEvent: string) => {
            const lines = rawEvent.split(/\r?\n/);
            const eventType = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
            const data = lines
                .filter(line => line.startsWith('data:'))
                .map(line => line.slice(5).trimStart())
                .join('\n');

            if (eventType !== 'step' || !data) return;

            try {
                handleStep(JSON.parse(data) as CreationStep);
            } catch (error) {
                console.error('Error parsing SSE event:', error);
                reportError('Risposta non valida dal servizio ordini.');
            }
        };

        void (async () => {
            try {
                const token = await acquireTokenSilent();
                const baseUrl = process.env.REACT_APP_API_URL_ORDERS || 'http://localhost:18082';
                const response = await fetch(
                    `${baseUrl}/api/orders/mexal/create-order-stream/${encodeURIComponent(order.orderId)}`,
                    {
                        headers: {
                            Accept: 'text/event-stream',
                            Authorization: `Bearer ${token}`
                        },
                        signal: controller.signal
                    }
                );

                if (!response.ok) {
                    throw new Error(`Il servizio ordini ha risposto con HTTP ${response.status}.`);
                }
                if (!response.body) {
                    throw new Error('Il servizio ordini non ha restituito uno stream.');
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    buffer += decoder.decode(value, { stream: !done });

                    const events = buffer.split(/\r?\n\r?\n/);
                    buffer = events.pop() || '';
                    events.forEach(processSseEvent);

                    if (done) break;
                }

                if (buffer) processSseEvent(buffer);
                if (!receivedTerminalEvent && !controller.signal.aborted) {
                    reportError('La connessione al servizio ordini si è chiusa prima del completamento.');
                }
            } catch (error) {
                if (controller.signal.aborted) return;

                console.error('SSE Error:', error);
                reportError(
                    'Impossibile creare l’ordine su Mexal.',
                    error instanceof Error ? error.message : undefined
                );
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
            }
        })();

        return () => controller.abort();
    }, [order, onSuccess]);

    useEffect(() => {
        if (opened && order) {
            const cleanup = startCreation();
            return cleanup;
        }
    }, [opened, order, startCreation]);

    const handleClose = () => {
        if (isCompleted || isError) {
            onClose();
        }
    };

    const getStepIcon = (step: CreationStep) => {
        if (step.status === 'IN_PROGRESS') {
            return <Loader size="xs" color="blue" />;
        }
        if (step.status === 'COMPLETED') {
            return (
                <ThemeIcon size="sm" radius="xl" color="teal" variant="filled">
                    <MaterialSymbol icon="check" size={14} />
                </ThemeIcon>
            );
        }
        if (step.status === 'ERROR') {
            return (
                <ThemeIcon size="sm" radius="xl" color="red" variant="filled">
                    <MaterialSymbol icon="close" size={14} />
                </ThemeIcon>
            );
        }
        return (
            <ThemeIcon size="sm" radius="xl" color="gray" variant="light">
                <MaterialSymbol icon="circle" size={14} />
            </ThemeIcon>
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={null}
            centered
            size="sm"
            closeOnClickOutside={isCompleted || isError}
            closeOnEscape={isCompleted || isError}
            withCloseButton={isCompleted || isError}
            styles={{
                header: { display: isCompleted || isError ? 'flex' : 'none' },
                body: { padding: '2rem' }
            }}
        >
            <Stack align="center" gap="lg">
                {/* Main icon/loader */}
                <Box>
                    {isCompleted ? (
                        <ThemeIcon size={80} radius="xl" color="teal" variant="light">
                            <MaterialSymbol icon="check_circle" size={50} fill color="teal" />
                        </ThemeIcon>
                    ) : isError ? (
                        <ThemeIcon size={80} radius="xl" color="red" variant="light">
                            <MaterialSymbol icon="error" size={50} fill color="red" />
                        </ThemeIcon>
                    ) : (
                        <Loader size={60} color="blue" type="dots" />
                    )}
                </Box>

                {/* Title */}
                <Text size="lg" fw={600} ta="center">
                    {isCompleted ? 'Ordine Creato!' : isError ? 'Errore' : 'Creazione Ordine Mexal'}
                </Text>

                {/* Steps list */}
                <Paper withBorder p="md" radius="md" w="100%">
                    <Stack gap="xs">
                        {steps.map((step, index) => (
                            <Group key={index} gap="sm" wrap="nowrap" align="flex-start">
                                {getStepIcon(step)}
                                <Box style={{ flex: 1 }}>
                                    <Text 
                                        size="sm" 
                                        fw={step.status === 'IN_PROGRESS' ? 500 : 400}
                                        c={step.status === 'IN_PROGRESS' ? 'blue' : step.status === 'COMPLETED' ? 'teal' : step.status === 'ERROR' ? 'red' : 'dimmed'}
                                    >
                                        {step.message}
                                    </Text>
                                    {step.details && step.status === 'COMPLETED' && (
                                        <Text size="xs" c="dimmed">{step.details}</Text>
                                    )}
                                    {step.details && step.status === 'ERROR' && (
                                        <Text size="xs" c="red.7" mt={2}>
                                            {step.details}
                                        </Text>
                                    )}
                                </Box>
                            </Group>
                        ))}
                        
                        {steps.length === 0 && !isError && (
                            <Center>
                                <Text size="sm" c="dimmed">Avvio in corso...</Text>
                            </Center>
                        )}
                    </Stack>
                </Paper>

                {/* Result - Success */}
                {isCompleted && documentId && (
                    <Paper withBorder p="md" radius="md" w="100%" bg="teal.0">
                        <Group justify="center" gap="xs">
                            <MaterialSymbol icon="description" size={20} color="teal" />
                            <Text size="lg" fw={600} c="teal.8">
                                {documentId}
                            </Text>
                        </Group>
                    </Paper>
                )}

                {/* Error actions */}
                {isError && (
                    <Group justify="center" gap="sm">
                        <Button 
                            variant="light" 
                            color="blue"
                            leftSection={<MaterialSymbol icon="refresh" size={18} />}
                            onClick={() => startCreation()}
                        >
                            Riprova
                        </Button>
                        <Button 
                            variant="subtle" 
                            color="gray"
                            onClick={handleClose}
                        >
                            Chiudi
                        </Button>
                    </Group>
                )}
            </Stack>
        </Modal>
    );
};

export default MexalOrderCreationModal;
